// src/components/AirDisplay.tsx

import React, { useState } from 'react';
import {
	Box,
	Button,
	List,
	ListItem,
	ListItemText,
	ListSubheader,
	Paper,
	Radio,
	Theme,
	Typography,
	IconButton,
	Tooltip,
	Link,
	Chip,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from '@mui/material';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import LuggageIcon from '@mui/icons-material/Luggage';
import BackpackIcon from '@mui/icons-material/Backpack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';

import makeStyles from '@mui/styles/makeStyles';

import { FlightsTableDesktop } from './FlightsTableDesktop';
import useBreakpoints from '../../../common/hooks/useBreakpoints';
import { FlightMobile } from './FlightMobile';
import FlightImagePreviewList from './FileImagePreviewList';
import { useSelector } from 'react-redux';
import { useAgencyBranding } from '../../branding';
import { RootState } from 'store';
import { countryWAPMapping } from 'constants/constants';
import { Air, AirSimple, BaggageTypes } from '../../types';
import { TstDialog } from './TstDialog';

const useStyles = makeStyles((theme: Theme) => ({
	container: {
		padding: theme.spacing(2),
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
	},
	submit: {
		margin: theme.spacing(3, 0, 2),
	},
}));

const baggageHelpContent = (
	<>
		<Typography variant="body2" paragraph>
			<strong>Artículo personal:</strong> mochila pequeña o bolso para colocar debajo del asiento delantero.
		</Typography>
		<Typography variant="body2" paragraph>
			<strong>Equipaje de mano:</strong> bolso, maleta o carry-on mediano para el compartimento superior.
		</Typography>
		<Typography variant="body2">
			<strong>Equipaje para despachar:</strong> maleta grande para facturación y bodega del avión.
		</Typography>
	</>
);

interface AirDisplayProps {
	data: Air | AirSimple;
	previewMode: boolean;
	index: number;
	showSelectionControl?: boolean;
	indexSelected?: number | null;
	handleSelectionChange?: (index: number | null) => void;
	handleDeleteFlight?: (index: number) => void;
	onEdit?: () => void;
}

export const AirDisplay = ({
	data,
	previewMode,
	index,
	showSelectionControl,
	indexSelected,
	handleSelectionChange,
	handleDeleteFlight,
	onEdit,
}: AirDisplayProps): JSX.Element | null => {
	if (!data) return null;
	const classes = useStyles();
	const branding = useAgencyBranding();
	const isMediumDown = useBreakpoints().mediumDown;
	const hasImages = data.images.length > 0;
	const hasFlightsInfo = Array.isArray(data?.data) && data?.data.length > 0;
	const handleClearAirConfig = () => {
		handleDeleteFlight && handleDeleteFlight(index);
	};

	const HeaderFlightSelection = () => (
		<Box
			onClick={() => handleSelectionChange && handleSelectionChange(index)}
			sx={{
				display: 'flex',
				cursor: 'pointer',
				justifyContent: 'space-between',
				alignItems: 'center',
				width: '100%',
				paddingLeft: '14px',
				paddingRight: '10px',
				paddingTop: '10px',
				paddingBottom: '10px',
				background:
					index === indexSelected
						? branding.headerGradient
						: 'linear-gradient(135deg, rgba(49, 25, 75, 0.88) 0%, rgba(49, 25, 75, 0.74) 100%)',
			}}
		>
			<Box>
				<Typography component="h1" variant="h6" sx={{ fontSize: '1rem', color: 'white', fontWeight: 800 }}>
					Opción de vuelo {index + 1}
				</Typography>
				{/* Mismo lenguaje que las cards de hotel: que se pueda elegir no se
				    deja implícito en el radio — este público necesita que se lo
				    digan con palabras. */}
				<Box display="flex" alignItems="center" gap={0.5}>
					{index === indexSelected && <CheckCircleIcon sx={{ fontSize: 15, color: 'secondary.main' }} />}
					<Typography variant="caption" sx={{ color: 'rgba(255,255,255,.85)', fontWeight: 700 }}>
						{data.validatingCarrier}
						{data.validatingCarrier ? ' · ' : ''}
						{index === indexSelected ? 'Vuelo seleccionado' : 'Tocar para seleccionar'}
					</Typography>
				</Box>
			</Box>
			<Radio
				style={{ marginLeft: 'auto', padding: '0' }}
				checked={indexSelected == index}
				onChange={() => handleSelectionChange && handleSelectionChange(index)}
				sx={{
					backgroundColor: 'white',
					color: 'inherit',
					'&.Mui-checked': { color: 'inherit', backgroundColor: 'white' },
				}}
			/>
		</Box>
	);

	const { auth } = useSelector((state: RootState) => state);
	const countryWAP = countryWAPMapping[auth.country] || '59897683318';
	const baggageUrl = `https://aerolineas.mevuelo.info/${auth.country}/${data.validatingCarrier}/equipaje`;
	const baggageMessage = `https://wa.me/${countryWAP}?text=Hola%2C%20quisiera%20saber%20m%C3%A1s%20acerca%20de%20la%20pol%C3%ADtica%20de%20equipaje%20de%20la%20cotizaci%C3%B3n%20que%20me%20enviaste.`;
	const checkedBaggageIncluded = data.baggage !== BaggageTypes.NO_BAGGAGE;
	const checkedBaggageDescription =
		data.baggage === BaggageTypes.TWO_X_23K
			? '2 piezas de hasta 23 kg cada una'
			: data.baggage === BaggageTypes.ONE_X_23K
			? '1 pieza de hasta 23 kg'
			: 'No incluida en esta tarifa';
	const baggagePolicyRows = [
		{
			icon: <BusinessCenterIcon />,
			title: 'Artículo personal',
			description: 'Mochila, cartera o bolso chico para ubicar debajo del asiento.',
			status: 'INCLUIDO',
			included: true,
		},
		{
			icon: <BackpackIcon />,
			title: 'Equipaje de mano',
			description: data.carryOn
				? 'Carry-on o valija chica para el compartimento superior. Medidas según aerolínea.'
				: 'No incluido en esta tarifa. Podemos cotizarlo antes de confirmar.',
			status: data.carryOn ? 'INCLUIDO' : 'NO INCLUIDO',
			included: data.carryOn,
		},
		{
			icon: <LuggageIcon />,
			title: 'Valija despachada',
			description: checkedBaggageDescription,
			status: checkedBaggageIncluded ? 'INCLUIDO' : 'NO INCLUIDO',
			included: checkedBaggageIncluded,
		},
	];

	const [isTstDialogOpen, setIsTstDialogOpen] = useState<boolean>(false);
	const handleOpenTstDialog = () => {
		setIsTstDialogOpen(true);
	};
	const handleCloseTstDialog = () => {
		setIsTstDialogOpen(false);
	};

	return (
		<Paper
			sx={{
				marginBottom: '20px',
				overflow: 'hidden',
				border: showSelectionControl && index === indexSelected ? '2px solid' : '1px solid',
				borderColor: showSelectionControl && index === indexSelected ? 'primary.main' : 'rgba(49, 25, 75, 0.12)',
				borderRadius: '12px',
				boxShadow:
					showSelectionControl && index === indexSelected
						? '0 14px 34px rgba(49, 25, 75, 0.24)'
						: '0 6px 18px rgba(49, 25, 75, 0.10)',
				backgroundColor: 'white',
			}}
		>
			<div className={classes.content}>
				<div>
					{hasFlightsInfo && !previewMode && !isMediumDown && (
						<div>
							{showSelectionControl && <HeaderFlightSelection />}
							<FlightsTableDesktop flightInfo={data.data || []} />
						</div>
					)}
					{hasFlightsInfo && (previewMode || isMediumDown) && (
						<div>
							{showSelectionControl && <HeaderFlightSelection />}
							<FlightMobile flightInfo={data.data || []} />
						</div>
					)}
					{hasImages && (
						<div>
							{showSelectionControl && <HeaderFlightSelection />}
							{<FlightImagePreviewList flightImages={data.images} />}
						</div>
					)}
					<div style={{ display: 'flex', flexDirection: 'row' }}>
						{!previewMode && (
							<List dense>
								<ListSubheader component="div">Carrier</ListSubheader>
								<ListItem>
									<ListItemText primary={data.validatingCarrier} />
								</ListItem>
							</List>
						)}
						<List
							dense
							style={{ width: previewMode ? '100%' : 'auto', flex: previewMode ? 'none' : 1 }}
							sx={{
								backgroundColor: previewMode ? 'rgba(49, 25, 75, 0.025)' : undefined,
								px: 1.25,
								pb: 1,
							}}
						>
							<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8 }}>
								<ListSubheader
									component="div"
									sx={{ bgcolor: 'transparent', color: 'primary.main', fontWeight: 800, px: 0 }}
								>
									Equipaje incluido
								</ListSubheader>
								{!isMediumDown && (
									<Tooltip title={baggageHelpContent} placement="right">
										<IconButton>
											<InfoIcon color="info" />
										</IconButton>
									</Tooltip>
								)}
							</div>
							<Box
								sx={{
									display: 'grid',
									gap: 1,
									px: 0,
									pb: 1,
								}}
							>
								{baggagePolicyRows.map((row) => (
									<Box
										key={row.title}
										sx={{
											display: 'grid',
											gridTemplateColumns: '38px 1fr auto',
											gap: 1.25,
											alignItems: 'center',
											p: 1.15,
											borderRadius: '10px',
											backgroundColor: 'white',
											border: '1px solid rgba(49, 25, 75, 0.10)',
										}}
									>
										<Box
											sx={{
												width: 38,
												height: 38,
												borderRadius: '10px',
												display: 'grid',
												placeItems: 'center',
												color: row.included ? 'success.main' : 'text.secondary',
												backgroundColor: row.included ? 'rgba(46, 125, 50, 0.08)' : 'rgba(108, 117, 125, 0.08)',
											}}
										>
											{row.icon}
										</Box>
										<Box minWidth={0}>
											<Typography variant="body2" color="primary" fontWeight={900} lineHeight={1.15}>
												{row.title}
											</Typography>
											<Typography
												variant="caption"
												color="text.secondary"
												fontWeight={600}
												lineHeight={1.25}
												display="block"
											>
												{row.description}
											</Typography>
										</Box>
										<Chip
											size="small"
											icon={row.included ? <CheckCircleIcon /> : <CancelIcon />}
											label={row.status}
											color={row.included ? 'success' : 'default'}
											variant={row.included ? 'outlined' : 'filled'}
											sx={{
												borderRadius: '8px',
												fontSize: 10,
												fontWeight: 900,
												'& .MuiChip-icon': { fontSize: 14 },
											}}
										/>
									</Box>
								))}
							</Box>

							<ListItem sx={{ display: 'none' }}>
								<CheckCircleIcon color="success" sx={{ marginRight: '4px' }} />
								<ListItemText primary="Artículo personal" />
							</ListItem>

							{data.carryOn ? (
								<ListItem sx={{ display: 'none' }}>
									<CheckCircleIcon color="success" sx={{ marginRight: '4px' }} />
									<ListItemText primary="Equipaje de mano" />
								</ListItem>
							) : (
								<ListItem sx={{ display: 'none' }}>
									<CancelIcon color="warning" sx={{ marginRight: '4px' }} />
									<ListItemText primary="No incluye equipaje de mano" />
								</ListItem>
							)}

							{data.baggage === BaggageTypes.NO_BAGGAGE && (
								<ListItem sx={{ display: 'none' }}>
									<CancelIcon color="warning" sx={{ marginRight: '4px' }} />
									<ListItemText primary="No incluye equipaje para despachar" />
								</ListItem>
							)}

							{data.baggage === BaggageTypes.ONE_X_23K && (
								<ListItem sx={{ display: 'none' }}>
									<CheckCircleIcon color="success" sx={{ marginRight: '4px' }} />
									<ListItemText primary="Equipaje en bodega 1 pieza hasta 23Kg" />
								</ListItem>
							)}

							{data.baggage === BaggageTypes.TWO_X_23K && (
								<ListItem sx={{ display: 'none' }}>
									<CheckCircleIcon color="success" sx={{ marginRight: '4px' }} />
									<ListItemText primary="Equipaje en bodega 2 piezas hasta 23Kg" />
								</ListItem>
							)}

							{previewMode && isMediumDown && (
								<Accordion
									disableGutters
									elevation={0}
									sx={{
										mt: 1,
										border: '1px solid rgba(49, 25, 75, 0.10)',
										borderRadius: '10px !important',
										backgroundColor: 'white',
										overflow: 'hidden',
										'&:before': { display: 'none' },
									}}
								>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon color="primary" />}
										sx={{
											minHeight: 42,
											'& .MuiAccordionSummary-content': { my: 1 },
										}}
									>
										<Typography variant="body2" fontWeight={800} color="primary">
											Más info sobre equipaje
										</Typography>
									</AccordionSummary>
									<AccordionDetails sx={{ pt: 0 }}>
										<Typography variant="body2" color="text.secondary">
											Las dimensiones y condiciones finales dependen de la aerolínea, ruta y tarifa emitida.
										</Typography>
										{(!data.carryOn || data.baggage === BaggageTypes.NO_BAGGAGE) && (
											<Typography variant="body2" sx={{ mt: 1.5 }}>
												Si lo deseás, podemos cotizar
												{!data.carryOn && <strong> equipaje de mano</strong>}
												{!data.carryOn && data.baggage === BaggageTypes.NO_BAGGAGE && ' y/o'}
												{data.baggage === BaggageTypes.NO_BAGGAGE && <strong> equipaje en bodega</strong>} antes de
												confirmar la reserva.
											</Typography>
										)}
										<Typography variant="body2" sx={{ mt: 1.5 }}>
											Ver política de la aerolínea{' '}
											<Link href={baggageUrl} target="_blank" rel="noopener noreferrer">
												<strong>acá</strong>
											</Link>{' '}
											o consultar por{' '}
											<Link href={baggageMessage} target="_blank" rel="noopener noreferrer">
												<strong>WhatsApp</strong>
											</Link>
											.
										</Typography>
									</AccordionDetails>
								</Accordion>
							)}
							{previewMode && !isMediumDown && (!data.carryOn || data.baggage === BaggageTypes.NO_BAGGAGE) && (
								<Typography variant="body2" fontSize={12} sx={{ padding: '12px' }}>
									*No te preocupes, en caso de que lo desees podemos agregar
									{!data.carryOn && <strong> equipaje de mano</strong>}
									{!data.carryOn && data.baggage === BaggageTypes.NO_BAGGAGE && ' y/o'}
									{data.baggage === BaggageTypes.NO_BAGGAGE && <strong> equipaje en bodega </strong>}
									con un costo adicional antes de confirmar la cotización, durante el proceso del web check-in o mismo
									en el aeropuerto antes de partir el viaje.
								</Typography>
							)}
							{previewMode && !isMediumDown && (
								<Typography variant="body2" fontSize={12} sx={{ padding: '12px', color: 'text.secondary' }}>
									Las medidas y condiciones finales dependen de la aerolínea. Podés verificar la política oficial{' '}
									<Link href={baggageUrl} target="_blank" rel="noopener noreferrer">
										<strong>haciendo click acá</strong>
									</Link>{' '}
									o consultar con tu asesor a través de{' '}
									<Link href={baggageMessage} target="_blank" rel="noopener noreferrer">
										<strong>whatsapp</strong>
									</Link>
									.
								</Typography>
							)}
						</List>
						{!previewMode && 'fareADT' in data && (
							<List dense>
								<ListSubheader component="div">Tarifas</ListSubheader>
								<div style={{ display: 'flex' }}>
									<ListItem>
										<ListItemText primary="Adultos" secondary={data.fareADT} />
									</ListItem>
									<ListItem>
										<ListItemText primary="CHD" secondary={data.fareCHD} />
									</ListItem>
									<ListItem>
										<ListItemText primary="INF" secondary={data.fareINF} />
									</ListItem>
									<ListItem>
										<ListItemText primary="Moneda" secondary="USD" />
									</ListItem>
								</div>
							</List>
						)}
					</div>
				</div>
			</div>

			{!previewMode && (
				<Box justifyContent="space-around" display="flex" pb={2}>
					<Button type="button" variant="outlined" color="secondary" onClick={handleClearAirConfig}>
						Eliminar opción
					</Button>
					<Button variant="outlined" color="secondary" onClick={onEdit} sx={{ mx: 1 }}>
						Editar opción
					</Button>
					{'tst' in data && data.tst && (
						<Button variant="outlined" color="secondary" onClick={handleOpenTstDialog}>
							Ver TST
						</Button>
					)}
				</Box>
			)}
			{'tst' in data && data.tst && <TstDialog open={isTstDialogOpen} tst={data.tst} onClose={handleCloseTstDialog} />}
		</Paper>
	);
};

export default AirDisplay;
